import type { User } from '../lib/types';

/**
 * Browser-only, non-secret session snapshot used to render the last known user
 * while the API is asleep or temporarily unreachable. Access and refresh
 * tokens are intentionally excluded; the server remains the authority for
 * every protected request.
 */
export const AUTH_SNAPSHOT_STORAGE_KEY = 'fintrack:auth-snapshot:v1';
const AUTH_SNAPSHOT_VERSION = 1;

interface PersistedAuthSnapshot {
  version: number;
  savedAt: number;
  user: User;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

const isPersistableUser = (value: unknown): value is User => {
  if (!isRecord(value)) return false;
  const requiredStrings = ['id', 'email', 'firstName', 'fullName', 'lastName', 'createdAt', 'updatedAt'];
  if (requiredStrings.some((key) => typeof value[key] !== 'string' || value[key] === '')) {
    return false;
  }
  return value.subscriptionTier === 'free' || value.subscriptionTier === 'premium';
};

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Read the last authenticated profile without contacting the backend. */
export const readPersistedAuthUser = (): User | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(AUTH_SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as Partial<PersistedAuthSnapshot>;
    if (snapshot.version !== AUTH_SNAPSHOT_VERSION || !isPersistableUser(snapshot.user)) {
      storage.removeItem(AUTH_SNAPSHOT_STORAGE_KEY);
      return null;
    }
    return snapshot.user;
  } catch {
    try {
      storage.removeItem(AUTH_SNAPSHOT_STORAGE_KEY);
    } catch {
      // Ignore storage failures; an anonymous in-memory state is safer.
    }
    return null;
  }
};

/**
 * Persist only the typed profile fields needed for offline rendering. This
 * explicit projection prevents future AuthResponse fields from becoming local
 * storage credentials by accident.
 */
export const persistAuthUser = (user: User | null): void => {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (!user) {
      storage.removeItem(AUTH_SNAPSHOT_STORAGE_KEY);
      return;
    }

    const safeUser: User = {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      fullName: user.fullName,
      lastName: user.lastName,
      subscriptionTier: user.subscriptionTier,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      ...(user.profilePicture !== undefined ? { profilePicture: user.profilePicture } : {}),
      ...(user.isEmailVerified !== undefined ? { isEmailVerified: user.isEmailVerified } : {}),
      ...(user.mfaEnabled !== undefined ? { mfaEnabled: user.mfaEnabled } : {}),
      ...(user.authProviders !== undefined ? { authProviders: user.authProviders } : {}),
      ...(user.preferences !== undefined ? { preferences: user.preferences } : {}),
    };

    storage.setItem(AUTH_SNAPSHOT_STORAGE_KEY, JSON.stringify({
      version: AUTH_SNAPSHOT_VERSION,
      savedAt: Date.now(),
      user: safeUser,
    } satisfies PersistedAuthSnapshot));
  } catch {
    // Local storage is an optimization, never a reason to fail auth.
  }
};

export const clearPersistedAuthUser = (): void => persistAuthUser(null);
